import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import { GeneralLoading } from '@hem/ui'
import { getEnvVariable } from '@hem/utils'
import { Hydrate, QueryClientProvider } from '@tanstack/react-query'
import i18n from '../i18n'
import { I18nextProvider } from 'react-i18next'
import NextNProgress from 'nextjs-progressbar'

import 'antd/dist/reset.css'
import '~/styles/global.css'

import { queryClient } from '~/config'
import { getCookie, setCookie } from 'cookies-next'
import { COOKIE_REFRESH_TOKEN, COOKIE_USER } from '@hem/constants'
import { useMemo, useState } from 'react'
import { auth } from '~/api'

const RenderFromBrowserApp = dynamic(
  () => import('~/root-app').then((m) => m.ClinicAtHomeRoot),
  {
    loading: () => <GeneralLoading />,
    ssr: false,
  }
)

export function useRefreshTokenHook() {
  const [credentials, setCredentials] = useState({
    accessToken: getCookie(COOKIE_USER) as string,
    refreshToken: getCookie(COOKIE_REFRESH_TOKEN) as string,
  })

  const { accessToken, refreshToken } = credentials;

  useMemo(() => {
    if (accessToken && refreshToken && accessToken.split('.').length == 3) {
      const expiredTokenTime = JSON.parse(atob(accessToken.split('.')[1]))

      setTimeout(async () => {
        const response = await auth.refreshToken(refreshToken, getEnvVariable().baseURL)
        setCredentials({
          accessToken: response.data.data?.access_token as string,
          refreshToken: response.data.data?.refresh_token as string,
        })
        const parsedRefreshToken = JSON.parse(atob(response.data.data?.refresh_token.split('.')[1]))
        setCookie(COOKIE_USER, response.data.data?.access_token, { maxAge: response.data.data?.expires_in })
        setCookie(COOKIE_REFRESH_TOKEN, response.data.data?.refresh_token,
          { maxAge: parsedRefreshToken.exp - Date.now() / 1000 }
        )
      }, (expiredTokenTime.exp - Date.now() / 1000 - 30) * 1000)
    }

    if (!accessToken && refreshToken) {
      auth.refreshToken(refreshToken, getEnvVariable().baseURL).then((response) => {
        setCredentials({
          accessToken: response.data.data?.access_token as string,
          refreshToken: response.data.data?.refresh_token as string,
        })
        const parsedRefreshToken = JSON.parse(atob(response.data.data?.refresh_token.split('.')[1]))
        setCookie(COOKIE_USER, response.data.data?.access_token, { maxAge: response.data.data?.expires_in })
        setCookie(COOKIE_REFRESH_TOKEN, response.data.data?.refresh_token,
          { maxAge: parsedRefreshToken.exp - Date.now() / 1000 }
        )
      })
    }
  }, [accessToken, refreshToken])

  return {
    accessToken,
    refreshToken,
  }
}


function ClinicAtHome(props: AppProps) {
  const { pendoAPIKey, isEnabledPendo, pendoAccountId, pendoAccountName } =
    getEnvVariable()

  useRefreshTokenHook()

  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={props.pageProps.dehydratedState}>
        <>
          {!isEnabledPendo ? (
            <></>
          ) : (
            <script
              dangerouslySetInnerHTML={{
                __html: `(function(apiKey){
                (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
                v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
                    o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
                    y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
                    z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
                  document.cookie = 'pendo_secure=true; SameSite=None; Secure';
                  pendo.initialize({
                      visitor: {
                          id: '${props?.pageProps?.user?.id}',
                          username: '${props?.pageProps?.user?.username}',
                          first_name: '${props?.pageProps?.user?.firstName}',
                          last_name: '${props?.pageProps?.user?.lastName}',
                          full_name: '${props?.pageProps?.user?.fullName}',
                          user_role: '${props?.pageProps?.user?.roles[0]?.id}',
                      },
                      account: {
                        id: '${pendoAccountId}',
                        name: '${pendoAccountName}',
                      }
                  });
          })('${pendoAPIKey}');
            `,
              }}
            />
          )}
          <NextNProgress />
          <I18nextProvider i18n={i18n}>
            <RenderFromBrowserApp {...props} />
          </I18nextProvider>
        </>
      </Hydrate>
    </QueryClientProvider>
  )
}

export default ClinicAtHome
